import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Modal from 'react-modal';
import Dropzone from 'react-dropzone';
import * as _ from 'lodash';
import { pushToast } from 'ducks/modules/toaster';
import { fetchFileStatus } from 'ducks/modules/file_status';
import constants from 'utils/constants';
import api from 'utils/api';
import LightLoader from 'components/light-loader/light-loader';
import Icon from 'components/icon/icon';
import 'components/upload-modal/upload-modal.scss';

class UploadModal extends React.Component {
    constructor(props) {
        super(props);
        this.views = {
            UPLOAD: 0,
            LOADING: 1,
        };
        this.state = {
            files: [],
            view: this.views.UPLOAD,
            report: null,
        };
        this.dropzoneRef = null;
    }

    componentDidMount() {
        Modal.setAppElement('#root');
    }

    onDrop(acceptedFiles, rejectedFiles) {
        if (rejectedFiles && rejectedFiles.length) {
            this.props.pushToast({
                title: `The following files are not allowed: ${rejectedFiles
                    .map((rejection) => rejection.file.name)
                    .join(', ')}`,
                type: constants.ToastTypes.ERROR,
            });
        }
        const filenames = this.state.files.map((file) => file.name).concat(acceptedFiles.map((file) => file.name));
        if (filenames.length !== _.uniq(filenames).length) {
            this.props.pushToast({
                title: 'Cannot select multiple files with the same name!',
                type: constants.ToastTypes.ERROR,
            });
            return;
        }
        this.setState({
            files: this.state.files.concat(acceptedFiles),
        });
    }

    removeFile(idx) {
        this.state.files.splice(idx, 1);
        this.setState({
            files: this.state.files,
        });
    }

    close() {
        this.setState({
            files: [],
            view: this.views.UPLOAD,
        });
        this.props.closeModal();
    }

    async upload() {
        try {
            this.setState({ view: this.views.LOADING });
            // get file upload URLS
            const response = await api.getDataUploadURL(this.state.files.map((file) => file.name));
            const fileUploadPromises = [];
            for (const key in response) {
                fileUploadPromises.push(
                    api.uploadFile(
                        response[key],
                        // key filename is of format <username>/<created_date>_<orig_filename>,
                        // so to find the corresponding file we must take the tail of this string
                        this.state.files.filter((file) => key.slice(key.length - file.name.length) === file.name)[0],
                    ),
                );
            }
            await Promise.all(fileUploadPromises);
            this.close();
            this.props.pushToast({
                title: 'Your file has been successfully uploaded and your data is now being processed. Please check the Blokur File Validation screen to see if your file has been validated or if any further action is required.',
                type: constants.ToastTypes.SUCCESS,
            });
            this.props.fetchFileStatus();
        } catch (err) {
            console.error('Error uploading files: ', err);
            this.close();
            this.props.pushToast({
                title: 'Error uploading files! If the problem persists, please contact support.',
                type: constants.ToastTypes.ERROR,
            });
        }
    }

    render() {
        return (
            <Modal
                portalClassName="bk-upload-modal"
                isOpen={this.props.isOpen}
                closeTimeoutMS={300}
                parentSelector={() => document.querySelector('#root')}
                className={{
                    base: 'bk-modal',
                    afterOpen: 'bk-modal__after-open',
                    beforeClose: 'bk-modal__before-close',
                }}
                overlayClassName={{
                    base: 'bk-modal-overlay',
                    afterOpen: 'bk-modal-overlay__after-open',
                    beforeClose: 'bk-modal-overlay__before-close',
                }}
                onRequestClose={this.props.closeModal}>
                <div className="bk-modal__title">
                    <Icon icon="upload-cloud" />
                    <h4>Upload Data</h4>
                    <span className="close" onClick={this.props.closeModal}>
                        <Icon icon="x" />
                    </span>
                </div>
                <div className="bk-modal__content">
                    <Dropzone
                        ref={(node) => (this.dropzoneRef = node)}
                        disabled={this.state.view === this.views.LOADING}
                        disabledClassName="disabled"
                        // accept="application/xml,text/xml"
                        multiple
                        noClick
                        onDrop={this.onDrop.bind(this)}
                        preventDropOnDocument>
                        {({ getRootProps, getInputProps, isDragAccept, isDragActive, isDragReject }) => {
                            let state;
                            if (isDragAccept) {
                                state = 'accept';
                            }
                            if (isDragActive) {
                                state = 'active';
                            }
                            if (isDragReject) {
                                state = 'reject';
                            }
                            return (
                                <div {...getRootProps({ className: `dropzone ${state || ''}` })}>
                                    <Icon icon="upload-cloud" />
                                    <input {...getInputProps()} />
                                    <p>Upload {this.state.files.length > 0 ? 'further' : ''} files</p>
                                    <ul className="selected-files">
                                        {this.state.files &&
                                            this.state.files.map((file, i) => (
                                                <li key={`file-${i}`}>
                                                    <span>{file.name}</span>
                                                    <span onClick={() => this.removeFile(i)}>
                                                        <Icon icon="x" />
                                                    </span>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            );
                        }}
                    </Dropzone>
                    <p>
                        Blokur currently accepts publishing data in <strong>CWR, CSV and Music Maestro formats</strong>.{' '}
                        <br />
                        Click{' '}
                        <a
                            target="blank"
                            href="https://s3-eu-west-1.amazonaws.com/blokur-public/templates/Blokur_CSV_Publishing_Data_Template_v3_0_0.xlsx">
                            here
                        </a>{' '}
                        to download a CSV template. Please note that this template file will open in excel format, and
                        you will be required to save your file as a CSV once you are ready to upload.
                    </p>
                    <button
                        className={`bk-button ${this.props.disabled ? 'disabled' : ''}`}
                        type="button"
                        onClick={() => {
                            this.dropzoneRef.open();
                        }}>
                        Choose files
                    </button>
                </div>
                <div className="bk-modal__actions">
                    <button
                        className={`bk-button cancel ${this.state.view === this.views.LOADING ? 'disabled' : ''}`}
                        onClick={() => this.close()}>
                        Cancel
                    </button>
                    {this.state.view !== this.views.LOADING && (
                        <button
                            className={`bk-button upload ${this.state.files.length === 0 ? 'disabled' : ''}`}
                            onClick={() => this.upload()}>
                            Upload
                        </button>
                    )}
                    {this.state.view === this.views.LOADING && <LightLoader size="12px" color="#585B5D" />}
                </div>
            </Modal>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    pushToast: (toast) => dispatch(pushToast(toast)),
    fetchFileStatus: () => dispatch(fetchFileStatus()),
});

UploadModal.propTypes = {
    closeModal: PropTypes.func,
    isOpen: PropTypes.bool,
    disabled: PropTypes.bool,
    pushToast: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(UploadModal);
